import { RefObject, useEffect, useRef, useState } from "react";
import { FocusableElement, tabbable } from "tabbable";

/**
 * Options for the useKeyboardFocusables hook
 */
type OptionProps =
  | {
      /**
       * Disable mutation observer
       */
      observeChange: false;
    }
  | {
      /**
       * Enable mutation observer with optional configuration
       */
      observeChange: true;
      /**
       * Observe attribute changes
       */
      attributes?: boolean;
      /**
       * Observe text content changes
       */
      characterData?: boolean;
      /**
       * Observe child element changes
       */
      childList?: boolean;
      /**
       * Observe descendant changes
       */
      subtree?: boolean;
    };

/**
 * Custom hook for tracking keyboard focusable elements within a container.
 *
 * Features:
 * - Tracks all keyboard focusable elements within a target container
 * - Supports both ref objects and direct element references
 * - Optional mutation observer for real-time updates
 * - Debounced updates to prevent excessive re-renders
 * - Configurable mutation observer options
 * - Manual update function for immediate refresh
 * - Uses tabbable library for accurate focusable detection
 * - Automatically cleans up mutation observer
 *
 * @param element - Target element or ref to monitor for focusable elements
 * @param options - Configuration options for mutation observer behavior
 * @returns Object containing current focusable elements and update function
 */
export const useKeyboardFocusables = (
  element: RefObject<HTMLElement> | HTMLElement | undefined,
  options: OptionProps = {
    observeChange: true,
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  },
) => {
  const [focusables, setFocusables] = useState<FocusableElement[] | null>(null);
  const observerRef = useRef<MutationObserver>();

  useEffect(() => {
    const target = element instanceof HTMLElement ? element : element?.current;
    if (!target) return;
    if (!options.observeChange && !focusables) {
      setFocusables(tabbable(target as Element));
      return;
    }

    const mutationCallback = () => {
      const targetArr = tabbable(target as Element);
      if (targetArr.length === 0) return;
      setFocusables(targetArr);
    };

    const observer = new MutationObserver(mutationCallback);
    observerRef.current = observer;
    observer.observe(target, {
      childList: true, // Observe changes to child elements
      subtree: true, // Observe changes to descendants of the target
      attributes: true, // Observe changes to attributes
      characterData: true, // Observe changes to text content
    });
    return () => {
      observer.disconnect();
    };
  }, [element, options, focusables]);

  return { focusables };
};
